import CanonicalUrl from '@atoms/CanonicalUrl/CanonicalUrl';
import { PREPR_PAGE_TYPES } from '@constants/constants';
import { getConfig } from '@lib/prepr/config';
import { getDynamicPage } from '@lib/prepr/dynamic';
import { getNotification } from '@lib/prepr/notification';
import { getRedirect } from '@lib/prepr/redirect';
import ActivityOverviewTemplate from '@templates/ActivityOverviewTemplate';
import ActivityTemplate from '@templates/ActivityTemplate';
import ContentTemplate from '@templates/ContentTemplate';
import EventOverviewTemplate from '@templates/EventsOverviewTemplate';
import EventTemplate from '@templates/EventTemplate';
import ExploreTemplate from '@templates/ExploreTemplate';
import LandingTemplate from '@templates/LandingTemplate';
import LocationOverviewTemplate from '@templates/LocationOverviewTemplate';
import LocationTemplate from '@templates/LocationTemplate';
import MapTemplate from '@templates/MapTemplate';
import NewsOverviewTemplate from '@templates/NewsOverviewTemplate';
import NewsTemplate from '@templates/NewsTemplate';
import RoutesOverviewTemplate from '@templates/RoutesOverviewTemplate';
import RouteTemplate from '@templates/RouteTemplate';
import SearchTemplate from '@templates/SearchTemplate';
import SharedListTemplate from '@templates/SharedListTemplate';
import ZigzagCampaignTemplate from '@templates/ZigzagCampaignTemplate';
import { Locale } from '@type-declarations/locale';
import {
  ActivityOverviewPage,
  ActivityPage,
  ContentPage,
  EventOverviewPage,
  EventPage,
  ExplorePage,
  LandingPage,
  LocationOverviewPage,
  LocationPage,
  MainCategoryPage,
  MapPage,
  NewsOverviewPage,
  NewsPage,
  RouteOverviewPage,
  RoutePage,
  SearchPage,
  SharedListPage,
  SubCategoryPage,
  SubSubCategoryPage,
  ZigzagCampaignPage,
} from '@type-declarations/page';
import getHeroVariant from '@utils/getHeroVariant';

function Page({
  page,
}: {
  page:
    | ActivityOverviewPage
    | ActivityPage
    | ContentPage
    | EventOverviewPage
    | EventPage
    | ExplorePage
    | LandingPage
    | LocationOverviewPage
    | LocationPage
    | MainCategoryPage
    | MapPage
    | NewsOverviewPage
    | NewsPage
    | RouteOverviewPage
    | RoutePage
    | SearchPage
    | SharedListPage
    | SubCategoryPage
    | SubSubCategoryPage
    | ZigzagCampaignPage;
}) {
  let template = null;

  switch (page.typename) {
    case PREPR_PAGE_TYPES.ACTIVITY_OVERVIEW_PAGE:
      template = <ActivityOverviewTemplate page={page} />;
      break;

    case PREPR_PAGE_TYPES.ACTIVITY_PAGE:
      template = <ActivityTemplate page={page} />;
      break;

    case PREPR_PAGE_TYPES.EXPLORE_PAGE:
      template = <ExploreTemplate page={page} />;
      break;

    case PREPR_PAGE_TYPES.LOCATION_PAGE:
      template = <LocationTemplate page={page} />;
      break;

    case PREPR_PAGE_TYPES.LOCATION_OVERVIEW_PAGE:
      template = <LocationOverviewTemplate page={page} />;
      break;

    case PREPR_PAGE_TYPES.EVENT_OVERVIEW_PAGE:
      template = <EventOverviewTemplate page={page} />;
      break;

    case PREPR_PAGE_TYPES.NEWS_OVERVIEW_PAGE:
      template = <NewsOverviewTemplate page={page} />;
      break;

    case PREPR_PAGE_TYPES.NEWS_PAGE:
      template = <NewsTemplate page={page} />;
      break;

    case PREPR_PAGE_TYPES.EVENT_PAGE:
      template = <EventTemplate page={page} />;
      break;

    case PREPR_PAGE_TYPES.ROUTE_OVERVIEW_PAGE:
      template = <RoutesOverviewTemplate page={page} />;
      break;

    case PREPR_PAGE_TYPES.ROUTE_PAGE:
      template = <RouteTemplate page={page} />;
      break;

    case PREPR_PAGE_TYPES.CONTENT_PAGE:
      template = <ContentTemplate page={page} />;
      break;

    case PREPR_PAGE_TYPES.SEARCH_PAGE:
      template = <SearchTemplate page={page} />;
      break;

    case PREPR_PAGE_TYPES.MAP_PAGE:
      template = <MapTemplate page={page} />;
      break;

    case PREPR_PAGE_TYPES.ZIGZAG_CAMPAIGN_PAGE:
      template = <ZigzagCampaignTemplate page={page} />;
      break;

    case PREPR_PAGE_TYPES.SHARED_LIST_PAGE:
      template = <SharedListTemplate page={page} />;
      break;

    case PREPR_PAGE_TYPES.LANDING_PAGE:
      template = <LandingTemplate page={page} />;
      break;

    case PREPR_PAGE_TYPES.MAIN_CATEGORY:
    case PREPR_PAGE_TYPES.SUB_CATEGORY:
    case PREPR_PAGE_TYPES.SUB_SUB_CATEGORY:
      template = <ContentTemplate page={page} />;
      break;

    default:
      template = <h1>Pagina niet gevonden</h1>;
  }

  return (
    <>
      <CanonicalUrl />
      {template}
    </>
  );
}

export async function getServerSideProps({
  req,
  locale,
  params,
  preview = false,
  query,
}: {
  req: { cookies: { __prepr_uid: string } };
  locale: Locale;
  params: { slug: string[] };
  preview: boolean;
  query: {
    page?: string;
    tags?: string;
    accessibility?: string;
    title?: string;
    period?: string;
    companion?: string;
    transport?: string;
    weather?: string;
    secret?: string;
  };
}) {
  const { __prepr_uid: customerId = '' } = req.cookies;
  const { slug } = params;

  // This makes sure we don't try to fetch a slug that is a file from Prepr
  if (slug[slug.length - 1].includes('.')) {
    console.warn("Trying to fetch a file which doesn't exist");
    return {
      notFound: true,
    };
  }

  const [data, config, redirect, notification] = await Promise.all([
    getDynamicPage({
      customerId,
      slug,
      locale,
      query,
      preview,
    }),
    getConfig({ locale, preview }),
    getRedirect({ locale, source: `/${slug.join('/')}` }),
    getNotification({ locale }),
  ]);

  if (redirect?.destination) {
    return {
      redirect: {
        destination: `/${locale}${redirect.destination}`,
        permanent: redirect.permanent,
      },
    };
  }

  if (!data.page) {
    console.warn('Page not found');
    return {
      notFound: true,
    };
  }

  if ('pageActivated' in data.page && data.page.pageActivated === false) {
    console.warn('Page not activated');

    return {
      notFound: true,
    };
  }

  return {
    props: {
      customerId,
      locale,
      preview,
      page: {
        ...data.page,
        exploreTags: data.exploreTags || [],
        targetTags: data.targetTags || [],
        highlightedTag: data.highlightedTag || null,
        categories: data.categories || [],
        items: data.items || [],
        total: data.total || 0,
        additionalSectionData: data.additionalSectionData || {},
        heroVariant: getHeroVariant(data.page.typename),
        featuredLocations: data.featuredLocations || [],
      },
      config,
      notification,
    },
  };
}

export default Page;
